import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, ModuleWithProviders, NgModule, Provider, Type } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessaging, AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, UrlSerializer } from '@angular/router';
import { ServiceWorkerModule, SwPush } from '@angular/service-worker';
import { PAGE_MAPPING } from '@kanalytics';
import { NAV_ITEM_LIST } from '@kbloc';
import { CONFIG } from '@kenv';
import { SocialChallengesModule } from '@kf-sc';
import { environment } from '@kp/../environments/environment';
import { AppRoutingModule } from '@kp/app-routing.module';
import { AppComponent } from '@kp/app.component';
import { ChallengesModule } from '@kp/challenges/challenges.module';
import { CoreModule } from '@kp/core/core.module';
import { PAGE_MAPPING_DATA } from '@kp/core/metrics/page-mapping-info';
import { CustomErrorHandler } from '@kp/error-handler/custom-error-handler.service';
import { httpInterceptorProviders } from '@kp/http-interceptors';
import { LocalResourcesModule } from '@kp/local-resources/local-resources.module';
import { OnboardingModule } from '@kp/onboarding/onboarding.module';
import { PastReflectionsModule } from '@kp/past-reflections/past-reflections.module';
import { ScreenMobileAppInfoModule } from '@kp/screen-mobile-app-info/screen-mobile-app-info.module';
import { Constants } from '@kp/shared/constants.service';
import { SharedModule } from '@kp/shared/shared.module';
import { JsonObject } from '@ktypes/models';
import { TooltipVariations } from '@ktypes/props';
import { LowerCaseUrlSerializer } from '@kutil';
import { TippyDirective, popperVariation, provideTippyConfig, tooltipVariation } from '@ngneat/helipopper';
import { TraceService } from '@sentry/angular';
import { UserIdleModule } from 'angular-user-idle';

const imports: Array<Type<any> | ModuleWithProviders<JsonObject> | any[]> = [
  AppRoutingModule,
  BrowserAnimationsModule,
  BrowserModule,
  ChallengesModule,
  CoreModule,
  HttpClientModule,
  LocalResourcesModule,
  OnboardingModule,
  PastReflectionsModule,
  ScreenMobileAppInfoModule,
  SocialChallengesModule,
  SharedModule,
  TippyDirective,
  UserIdleModule.forRoot({
    idle: environment.idleTimeConfig.timeIdle,
    timeout: environment.idleTimeConfig.timeOut,
    ping: environment.idleTimeConfig.timePing,
  }),
];
const providers: Provider[] = [
  { provide: 'Window', useValue: window },
  {
    provide: TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {
      /* no-op */
    },
    deps: [TraceService],
    multi: true,
  },
  { provide: ErrorHandler, useClass: CustomErrorHandler },
  Title,
  httpInterceptorProviders,
  { provide: CONFIG, useValue: environment },
  { provide: NAV_ITEM_LIST, useValue: Constants.navItemList },
  { provide: PAGE_MAPPING, useValue: PAGE_MAPPING_DATA },
  provideTippyConfig({
    defaultVariation: 'primary',
    variations: {
      tooltip: tooltipVariation,
      popper: popperVariation,
      primary: TooltipVariations.primary,
      primaryInfo: TooltipVariations.primaryInfo,
      challengeMenu: TooltipVariations.challengeMenu,
    },
  }),
  {
    provide: UrlSerializer,
    useClass: LowerCaseUrlSerializer,
  },
];

if (environment.production) {
  // for production environments (i.e. all hosted environments like dev/test/staging/production),
  // register imports and providers needed for service workers
  imports.push(
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: true }),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireMessagingModule
  );
  providers.push(AngularFireMessaging);
} else {
  // for non-production environments (i.e. local development), don't register service workers and
  // give it a "fake" version of AngularFireMessaging and SwPush for Injection purposes
  providers.push({ provide: AngularFireMessaging, useValue: {} }, { provide: SwPush, useValue: {} });
}

@NgModule({
  declarations: [AppComponent],
  imports,
  providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
